import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import ActivityScreen from "../../../../../components/screens/Activity/ActivityScreen";
import { DateTime } from "luxon";
import {
  courses as courseQuerys,
  completions as completionQuerys,
  progress as progressQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { getTopicProgress } from "@nualang/nualang-api-and-queries/APIs/Progress";
import { getTopicCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import {
  courses as courseHelpers,
  completions as completionHelpers,
  progress as progressHelpers,
} from "../../../../../utils/helpers";
import config from "../../../../../config";
function Topic(props) {
  const [filterName, setFilterName] = useState("");
  const [fromDate, setFromDate] = React.useState(
    DateTime.now().minus({ years: 1 }),
  );
  const [toDate, setToDate] = React.useState(DateTime.now());

  const {
    authData: { username, attributes },
    handleViewExerciseAttempt,
  } = props;
  const params = useParams();
  const { courseId, sectionId, topicId } = params;

  const { t } = useTranslation();

  const fromDateChange = (date) => {
    setFromDate(date);
  };

  const toDateChange = (date) => {
    setToDate(date);
  };

  const filterChange = (filter) => {
    setFilterName(filter);
  };

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(courseId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};
  const isCreator =
    course.createdBy === username ||
    (course.collaborators &&
      Array.isArray(course.collaborators) &&
      course.collaborators.includes(attributes.email));

  const topicProgressQuery = progressQuerys.useTopicProgress(
    async () => {
      const topicProgress = await getTopicProgress(
        courseId,
        sectionId,
        topicId,
      );
      let formattedProgress = await progressHelpers.formatProgress(
        topicProgress.Items.filter(
          (item) => isCreator || item.memberId === username,
        ),
        username,
        attributes.preferred_username,
      );
      if (fromDate || filterName) {
        if (filterName !== "" && fromDate) {
          formattedProgress = formattedProgress.filter(
            (item) =>
              isCreator &&
              item.createdByName
                .toLowerCase()
                .includes(filterName.toLowerCase()) &&
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        } else if (!filterName && fromDate) {
          formattedProgress = formattedProgress.filter(
            (item) =>
              item.createdAt >= fromDate.toJSDate().getTime() &&
              item.createdAt <= toDate.toJSDate().getTime(),
          );
        }
      }
      return formattedProgress;
    },
    {
      courseId,
      sectionId,
      topicId,
      filters: {
        fromDate,
        toDate,
        filterName,
      },
    },
    { enabled: !!(courseId && sectionId && topicId) && courseQuery.isSuccess },
  );

  const topicProgress =
    topicProgressQuery.isSuccess &&
    topicProgressQuery.data &&
    topicProgressQuery.data.length
      ? topicProgressQuery.data
      : [];

  const title =
    topicProgress.length && topicProgress[0].topicName
      ? topicProgress[0].topicName
      : "";
  const description =
    topicProgress.length && topicProgress[0].description
      ? topicProgress[0].description
      : "";

  const courseSectionTopicCompletionsQuery =
    completionQuerys.useTopicMemberCompletions(
      async () => {
        const memberCompletions = await getTopicCompletions(
          courseId,
          sectionId,
          topicId,
          filterName,
          fromDate,
          toDate,
        );
        let formattedCompletions = await completionHelpers.formatCompletions(
          memberCompletions.Items.filter(
            (item) => isCreator || item.memberId === username,
          ),
          username,
          attributes.preferred_username,
        );
        if (filterName || fromDate) {
          if (filterName !== "" && fromDate) {
            formattedCompletions = formattedCompletions.filter(
              (item) =>
                isCreator &&
                item.createdByName
                  .toLowerCase()
                  .includes(filterName.toLowerCase()) &&
                item.createdAt >= fromDate.toJSDate().getTime() &&
                item.createdAt <= toDate.toJSDate().getTime(),
            );
          } else if (!filterName && fromDate) {
            formattedCompletions = formattedCompletions.filter(
              (item) =>
                item.createdAt >= fromDate.toJSDate().getTime() &&
                item.createdAt <= toDate.toJSDate().getTime(),
            );
          }
        }
        return formattedCompletions;
      },
      {
        courseId,
        sectionId,
        topicId,
        filterName,
        fromDate,
        toDate,
      },
      {
        enabled: !!(courseId && sectionId && topicId && course),
      },
    );
  const courseSectionTopicCompletions =
    courseSectionTopicCompletionsQuery.isSuccess &&
    courseSectionTopicCompletionsQuery.data &&
    courseSectionTopicCompletionsQuery.data.length
      ? courseSectionTopicCompletionsQuery.data
      : [];
  return (
    <ActivityScreen
      t={t}
      filterChange={filterChange}
      fromDate={fromDate}
      toDate={toDate}
      toDateChange={toDateChange}
      fromDateChange={fromDateChange}
      isCreator={isCreator}
      picture={course.picture}
      title={title}
      description={description}
      completions={courseSectionTopicCompletions}
      completionsTotal={courseSectionTopicCompletions.length}
      progress={topicProgress}
      progressTotal={topicProgress.length}
      handleViewEvent={handleViewExerciseAttempt}
      isCompletionsLoading={courseSectionTopicCompletionsQuery.isLoading}
      isProgressLoading={topicProgressQuery.isLoading}
      mainSiteUrl={config.SITE_URL}
    />
  );
}

Topic.propTypes = {
  authData: PropTypes.object,
  themeContext: PropTypes.object,
  handleViewExerciseAttempt: PropTypes.func,
};

export default Topic;
